import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { Store } from '@ngrx/store';

import { Observable, Subscription } from 'rxjs';

import { AppState } from '../../../../../state';
import { Study } from '../../../../../models/study';
import { StudyType } from '../../../../../models/study-type';
import { Module } from '../../../../../models/module';
import { Metrics } from '../../../../../models/metrics';
import { Intervention } from '../../../../../models/intervention';
import {
  selectInterventionAnswersDownloaded,
  selectInterventionAnswersTimingDownloaded,
  selectIsDownloadingInterventionAnswers,
  selectIsInterventionAnswersTimingDownloading,
  selectMetrics,
} from '../../../../state/metrics/metrics.selectors';
import { ModuleItemAvailableSubType } from '../../../../../models/module-item-sub-type';
import {
  downloadInterventionAnswersTiming,
  downloadInterventionFiles,
} from '../../../../state/metrics/metrics.actions';
import { Language } from '../../../../../models/language';
import { selectSelectedLanguage } from '../../../../state/study/study.selectors';
import { MatDialog } from '@angular/material/dialog';
import { MetricsDownloadDialogComponent } from '../../../components/metrics-download-dialog/metrics-download-dialog.component';

export const KEYS_FOR_DOWNLOAD_FILTER = {
  AUDIO: 'audiofiles',
  IMAGES: 'svgImages',
};

@Component({
  selector: 'dashboard-metrics-download',
  templateUrl: './metrics-download.component.html',
  styleUrls: ['./metrics-download.component.scss'],
})
export class MetricsDownloadComponent implements OnInit, OnDestroy {
  @Input() selectedStudy: Study;

  StudyType = StudyType;

  downloadingInterventionAnswers$: Observable<boolean>;
  interventionAnswersDownloaded$: Observable<boolean>;
  downloadingInterventionAnswersTiming: Observable<boolean>;
  interventionAnswersTimingDownloaded$: Observable<boolean>;

  metrics: Metrics;
  interventions: Intervention[];
  modules: Module[];
  selectedLanguage: Language;

  selectedIntervention: Intervention;
  selectedModuleWithAudio: Module;
  selectedModuleWithNetworkOrOrbits: Module;

  KEYS_FOR_DOWNLOAD_FILTER = KEYS_FOR_DOWNLOAD_FILTER;

  private subscriptions = new Subscription();

  constructor(private store: Store<AppState>, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.interventions = this.selectedStudy?.interventions;
    this.modules = this.selectedStudy?.modules;

    this.downloadingInterventionAnswers$ = this.store.select(selectIsDownloadingInterventionAnswers);
    this.interventionAnswersDownloaded$ = this.store.select(selectInterventionAnswersDownloaded);
    this.downloadingInterventionAnswersTiming = this.store.select(selectIsInterventionAnswersTimingDownloading);
    this.interventionAnswersTimingDownloaded$ = this.store.select(selectInterventionAnswersTimingDownloaded);

    this.subscriptions.add(this.store.select(selectMetrics).subscribe((metrics) => (this.metrics = metrics)));
    this.subscriptions.add(
      this.store
        .select(selectSelectedLanguage)
        .subscribe((selectedLanguage) => (this.selectedLanguage = selectedLanguage))
    );
  }

  getModulesWithSpecificItems(keyForFilter: string, intervention: Intervention): Module[] {
    const modulesWithSpecificItems = [];
    const interventionModulesWithItems = [];

    intervention?.modules?.map((interventionModule) =>
      interventionModulesWithItems.push(this.modules?.find((module) => module.id === interventionModule.id))
    );

    interventionModulesWithItems?.forEach((interventionModuleWithItems) => {
      const wantedItem = interventionModuleWithItems?.moduleItems?.find((moduleItem) =>
        keyForFilter === KEYS_FOR_DOWNLOAD_FILTER.AUDIO
          ? moduleItem?.subType === ModuleItemAvailableSubType.AUDIO_RECORDING
          : moduleItem?.subType === ModuleItemAvailableSubType.NETWORK ||
            moduleItem?.subType === ModuleItemAvailableSubType.ORBITS ||
            moduleItem?.subType === ModuleItemAvailableSubType.IMAGE
      );
      if (wantedItem) {
        modulesWithSpecificItems.push(interventionModuleWithItems);
      }
    });

    return modulesWithSpecificItems;
  }

  setSelectedModuleSpecificModule(keyForFilter: string, module: Module): void {
    keyForFilter === KEYS_FOR_DOWNLOAD_FILTER.AUDIO
      ? (this.selectedModuleWithAudio = module)
      : (this.selectedModuleWithNetworkOrOrbits = module);
  }

  downloadInterventionAnswers(intervention: Intervention): void {
    this.selectedIntervention = intervention;
    this.dialog.open(MetricsDownloadDialogComponent, {
      data: {
        intervention,
        code: this.selectedLanguage?.code,
      },
      minWidth: '50%',
      height: '300px',
    });
  }

  downloadInterventionAnswersTiming(intervention: Intervention): void {
    this.selectedIntervention = intervention;
    this.store.dispatch(
      downloadInterventionAnswersTiming({
        interventionId: intervention?.id,
      })
    );
  }

  downloadInterventionFiles(keyForFiles: string, intervention: Intervention): void {
    this.store.dispatch(
      downloadInterventionFiles({
        keyForFiles,
        studyId: this.selectedStudy?.id,
        interventionId: intervention?.id,
        moduleId:
          keyForFiles === KEYS_FOR_DOWNLOAD_FILTER.AUDIO
            ? this.selectedModuleWithAudio?.id
            : this.selectedModuleWithNetworkOrOrbits?.id,
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
